import React, { useEffect, useState } from 'react';
import { Plus, Send, Eye } from 'lucide-react';
import { Card, Btn, Input, Field, Modal, Select, Table, Spinner, ErrBox } from '../../../components/UI';
import { inventoryAPI } from '../../../utils/api';
import { fmtDate } from '../../../utils/format';

export default function DistributionTab() {
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [viewDist, setViewDist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ from_warehouse_id: '', to_unit_id: '', distribution_date: new Date().toISOString().split('T')[0], requested_by: '', items: [] });
  const [currentItem, setCurrentItem] = useState({ item_id: '', quantity: 0, unit_cost: 0 });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [locsRes, itemsRes, distRes] = await Promise.all([
        inventoryAPI.getLocations(),
        inventoryAPI.getItems('?limit=100'),
        inventoryAPI.getDistributions(),
      ]);
      setLocations((locsRes.data || []).map(x => ({ value: x.id, label: x.nama })));
      setItems((itemsRes.data || []).map(x => ({ value: x.id, label: x.nama })));
      setDistributions(distRes.data || []);
      try {
        const unitRes = await fetch('/api/master/unit').then(r => r.json()).catch(() => ({ data: [] }));
        setUnits((unitRes.data || []).map(x => ({ value: x.id, label: x.nama })));
      } catch {}
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleAddItem = () => {
    if (!currentItem.item_id || !currentItem.quantity) return alert('Barang dan qty wajib diisi');
    setForm({ ...form, items: [...form.items, { ...currentItem }] });
    setCurrentItem({ item_id: '', quantity: 0, unit_cost: 0 });
  };

  const handleRemoveItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const handleSave = async () => {
    if (!form.from_warehouse_id || !form.to_unit_id || !form.distribution_date) return alert('Gudang asal, unit tujuan, dan tanggal wajib diisi');
    if (form.items.length === 0) return alert('Minimal 1 item harus ditambahkan');
    setSaving(true);
    try {
      await inventoryAPI.createDistribution(form);
      closeModal();
      loadData();
    } catch (e) { alert('Gagal menyimpan: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleView = async (id) => {
    try {
      const r = await inventoryAPI.getDistribution(id);
      setViewDist(r.data);
    } catch (e) { alert(e.message); }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ from_warehouse_id: '', to_unit_id: '', distribution_date: new Date().toISOString().split('T')[0], requested_by: '', items: [] });
    setCurrentItem({ item_id: '', quantity: 0, unit_cost: 0 });
  };

  const getStatusBadge = (status) => {
    const cfg = { DRAFT: { bg: '#F3F4F6', color: '#374151' }, DISETUJUI: { bg: '#ECFDF5', color: '#059669' }, DITOLAK: { bg: '#FEF2F2', color: '#DC2626' } };
    const c = cfg[status] || cfg.DRAFT;
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{status}</span>;
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>;
  if (error) return <div style={{ marginBottom: 16 }}><ErrBox msg={error} onRetry={loadData} /></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Btn icon={<Plus size={14}/>} onClick={() => setShowModal(true)}>Distribusi Baru</Btn>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <Send size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Distribusi Barang ({distributions.length})
          </h3>
        </div>
        {distributions.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            <Send size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Belum ada data distribusi</p>
          </div>
        ) : (
          <Table pageSize={10} cols={[
            { key: 'distribution_no', label: 'No. Distribusi', nowrap: true },
            { key: 'distribution_date', label: 'Tanggal', render: v => fmtDate(v) },
            { key: 'dari_gudang', label: 'Dari Gudang' },
            { key: 'ke_unit', label: 'Ke Unit' },
            { key: 'status', label: 'Status', render: v => getStatusBadge(v) },
            { key: 'id', label: 'Aksi', render: (id) => (
              <Btn size="sm" variant="secondary" icon={<Eye size={12}/>} onClick={() => handleView(id)}>Detail</Btn>
            )},
          ]} data={distributions} />
        )}
      </Card>

      {showModal && (
        <Modal title="Distribusi Barang Baru" onClose={closeModal} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Tanggal" required>
              <Input type="date" value={form.distribution_date} onChange={e => setForm({ ...form, distribution_date: e.target.value })} />
            </Field>
            <Field label="Requested By">
              <Input value={form.requested_by} onChange={e => setForm({ ...form, requested_by: e.target.value })} placeholder="Nama pemohon" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Dari Gudang" required>
              <Select value={form.from_warehouse_id} onChange={e => setForm({ ...form, from_warehouse_id: e.target.value })} options={locations} placeholder="Pilih gudang..." />
            </Field>
            <Field label="Ke Unit" required>
              <Select value={form.to_unit_id} onChange={e => setForm({ ...form, to_unit_id: e.target.value })} options={units} placeholder="Pilih unit..." />
            </Field>
          </div>

          <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 16, margin: '16px 0' }}>
            <h4 style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: '#374151' }}>Tambah Item</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 10, alignItems: 'flex-end' }}>
              <Field label="Barang">
                <Select value={currentItem.item_id} onChange={e => setCurrentItem({ ...currentItem, item_id: e.target.value })} options={items} placeholder="Pilih..." />
              </Field>
              <Field label="Qty">
                <Input type="number" value={currentItem.quantity || ''} onChange={e => setCurrentItem({ ...currentItem, quantity: parseFloat(e.target.value) || 0 })} />
              </Field>
              <Field label="Biaya/Unit">
                <Input type="number" value={currentItem.unit_cost || ''} onChange={e => setCurrentItem({ ...currentItem, unit_cost: parseFloat(e.target.value) || 0 })} />
              </Field>
              <Btn size="sm" onClick={handleAddItem}>+ Tambah</Btn>
            </div>
          </div>

          {form.items.length > 0 && (
            <Card style={{ margin: '12px 0', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: '#F8FAFC' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Barang</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Qty</th>
                  <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Biaya</th>
                  <th style={{ padding: '8px 12px', width: 50 }}></th>
                </tr></thead>
                <tbody>
                  {form.items.map((item, idx) => {
                    const itemData = items.find(i => i.value === item.item_id);
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                        <td style={{ padding: '8px 12px' }}>{itemData?.label}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{item.quantity}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{item.unit_cost}</td>
                        <td style={{ padding: '8px 12px' }}><Btn size="sm" variant="danger" onClick={() => handleRemoveItem(idx)}>X</Btn></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</Btn>
          </div>
        </Modal>
      )}

      {viewDist && (
        <Modal title={`Detail Distribusi ${viewDist.distribution_no}`} onClose={() => setViewDist(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Tanggal</strong><p style={{ margin: 2 }}>{fmtDate(viewDist.distribution_date)}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Dari Gudang</strong><p style={{ margin: 2 }}>{viewDist.dari_gudang}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Ke Unit</strong><p style={{ margin: 2 }}>{viewDist.ke_unit}</p></div>
            <div><strong style={{ fontSize: 12, color: '#6B7280' }}>Status</strong><p style={{ margin: 2 }}>{getStatusBadge(viewDist.status)}</p></div>
          </div>
          {viewDist.details?.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#F8FAFC' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Barang</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Qty Diminta</th>
                <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#6B7280', fontSize: 11 }}>Unit Cost</th>
              </tr></thead>
              <tbody>
                {viewDist.details.map((d, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px 12px' }}>{d.item_nama}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right' }}>{d.quantity_requested}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>{d.unit_cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setViewDist(null)}>Tutup</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}